'use client';

import { FC, Fragment, useRef } from 'react';
import clsx from 'clsx';
import TopSellingCard from '@/components/Cards/TopSellingCard';
import { ResponsiveCarousel } from '@base/components/src/Carousel';
import useDeviceMedia from '@/hooks/useDeviceMedia';
import { TopSellingProduct, TopSellingProducts, clickTrackingURL } from '@/types/algonomy';
import { AlgonomyCarouselProps } from './types';

import styles from './AlgonomyCarousel.module.css';

const AlgonomyCarousel: FC<AlgonomyCarouselProps> = ({
  placement,
  id,
  preloadIndex,
  isMobile,
  carouselContainerClass,
  carouselHeightClass,
  carouselPaddingClass,
  sliderWrapperClass,
  className,
  handleAddToCart,
  handleOpenServiceModal,
  handleOnViewAdapter,
  addToCartButton = false,
  backArrowClasses,
  nextArrowClasses,
}) => {
  const { strategyMessage, recommendedProducts } = placement;
  const mobile = useDeviceMedia('(max-width:1279px)', isMobile);

  const seenSlides = useRef<Array<boolean>>(new Array(recommendedProducts.length).fill(false));

  const handleSelectCard = (
    clickUrl: clickTrackingURL,
    linkUrl: string,
    relatedProductsImperative: boolean,
    serviceId?: string | undefined,
    serviceType?: string | undefined,
    productType?: string | undefined,
    serviceSku?: string | undefined,
  ) => {
    fetch(clickUrl || '')
      .catch((error) => {
        console.log('can not send tracking url data to algonomy from algonomy carousel', error, clickUrl);
      })
      .finally(() => {
        if (productType === 'Services' && serviceId && serviceType && serviceSku) {
          handleOpenServiceModal?.(serviceId, serviceType, relatedProductsImperative, serviceSku);
          return;
        }
        window.location.href = linkUrl;
      });
  };

  const handleRightClickCard = (clickUrl: clickTrackingURL) => {
    fetch(clickUrl || '').catch((error) => {
      console.log('can not send tracking url data to algonomy from algonomy carousel', error, clickUrl);
    });
  };

  const renderTopSellingCarousel = (
    recommendedProductItem: TopSellingProduct,
    idx: number,
    visibleSlides: number,
    currentSlide: number,
    addToCartButton: boolean,
  ) => {
    if (idx >= currentSlide && idx < currentSlide + visibleSlides) {
      seenSlides.current[idx] = true;
    }
    return (
      <Fragment key={idx}>
        {seenSlides.current[idx] ? (
          <TopSellingCard
            addToCartButton={addToCartButton}
            searchItem={recommendedProductItem}
            idx={idx}
            priorityIndex={preloadIndex}
            onClick={handleSelectCard}
            onContextMenu={handleRightClickCard}
            handleAddToCart={handleAddToCart}
            onViewAdapter={handleOnViewAdapter}
          />
        ) : (
          <div key={idx} className={styles.carouselPadding}></div>
        )}
      </Fragment>
    );
  };

  return recommendedProducts?.length > 0 ? (
    <div
      className={clsx(`${styles.categoryTopSellings} topSellingCarousel`, carouselContainerClass)}
      data-testid={id}
      id={id}
    >
      {strategyMessage && (
        <div className={`${styles.categoryCarouselTitle} topSellingCarouselTitle`}>{strategyMessage}</div>
      )}
      <ResponsiveCarousel<TopSellingProducts>
        dragStep={mobile ? 2 : 5}
        slideData={recommendedProducts}
        visibleSlidesQuery={{
          xxl: 5,
          xl: 4,
          lg: 3,
          md: 3,
          sm: 2,
          xs: 2,
        }}
        carouselPadding={clsx(styles.carouselPadding, carouselPaddingClass)}
        carouselHeight={clsx(styles.carouselHeight, carouselHeightClass)}
        hideDotButtons={!mobile}
        hideArrow={mobile}
        slideContentClassName={styles.slideContent}
        className={clsx(styles.carouselCategory, className)}
        arrowClassName={styles.arrowNavButton}
        buttonsAbsolute
        sliderWrapperClass={clsx(sliderWrapperClass)}
        backArrowClasses={clsx(backArrowClasses)}
        nextArrowClasses={clsx(nextArrowClasses)}
        renderSlide={(
          recommendedProductItem: TopSellingProduct,
          idx: number,
          visibleSlidesSlide: number,
          currentSlide: number,
        ) => renderTopSellingCarousel(recommendedProductItem, idx, visibleSlidesSlide, currentSlide, addToCartButton)}
      />
    </div>
  ) : null;
};

export default AlgonomyCarousel;
